import type { MouseEvent } from 'react';
import { format } from 'date-fns';
import { DATE_FORMAT_ISO_DAY } from '../../../constants/dateFormat';
import { excerpt } from '../../../utils/text';
import { formatPublicationTime } from '../functions';
import { snapshotRectFromClosest } from '../calendarCellAnchor';
import { getCalendarPillStyle } from '../pillStyles';
import type { CalendarDayLabel } from '../calendarTemporalLabels';
import type {
    CalendarPublication,
    CalendarPublicationHoverAnchor,
    PopoverPosition,
} from '../types';

import { DAY_LIST_POPOVER_MAX_WIDTH } from '../calendarCellAnchor';

const DAY_LIST_PANEL_SELECTOR = '[data-calendar-day-list-panel]';
const POPOVER_VIEWPORT_MARGIN = 12;
const POPOVER_MAX_HEIGHT = 400;

const BACKDROP_CLASS = 'fixed inset-0 z-[1090] cursor-default bg-transparent border-0 p-0';
const PANEL_CLASS =
    'fixed z-[1100] w-[min(100vw-24px,400px)] max-h-[min(70vh,400px)] overflow-y-auto rounded-md border border-light-gray bg-white p-3 shadow-lg';
const HEADER_CLASS =
    'mb-2 pb-2 border-b border-light-gray text-medium font-medium capitalize text-dark-gray';
const EMPTY_CLASS = 'text-more-small text-gray';
const LIST_CLASS = 'list-none space-y-2';
const ROW_BASE_CLASS =
    'w-full flex gap-1 cursor-pointer rounded-md p-1.5 font-medium text-[11px] transition-opacity hover:opacity-80';

function clampDayListPopoverPosition(position: PopoverPosition): PopoverPosition {
    const width = Math.min(window.innerWidth - 24, DAY_LIST_POPOVER_MAX_WIDTH);
    const maxHeight = Math.min(window.innerHeight * 0.7, POPOVER_MAX_HEIGHT);
    const top = Math.max(
        POPOVER_VIEWPORT_MARGIN,
        Math.min(position.top, window.innerHeight - maxHeight - POPOVER_VIEWPORT_MARGIN),
    );
    const left = Math.max(
        POPOVER_VIEWPORT_MARGIN,
        Math.min(position.left, window.innerWidth - width - POPOVER_VIEWPORT_MARGIN),
    );
    return { top, left };
}

function publicationHoverAnchorFromRow(
    event: MouseEvent<HTMLButtonElement>,
): CalendarPublicationHoverAnchor {
    return {
        clientX: event.clientX,
        clientY: event.clientY,
        cellRect: snapshotRectFromClosest(event, DAY_LIST_PANEL_SELECTOR),
        keepDayListOpen: true,
    };
}

type MonthDayListPopoverProps = Readonly<{
    day: Date;
    dayLabel: CalendarDayLabel | undefined;
    publications: readonly CalendarPublication[];
    position: PopoverPosition;
    onClose: () => void;
    onPublicationHoverStart: (
        publication: CalendarPublication,
        anchor: CalendarPublicationHoverAnchor,
    ) => void;
    onPublicationHoverEnd: () => void;
    onPublicationTap: (
        publication: CalendarPublication,
        anchor: CalendarPublicationHoverAnchor,
    ) => void;
}>;

export function MonthDayListPopover({
    day,
    dayLabel,
    publications,
    position,
    onClose,
    onPublicationHoverStart,
    onPublicationHoverEnd,
    onPublicationTap,
}: MonthDayListPopoverProps) {
    const fallbackDate = format(day, DATE_FORMAT_ISO_DAY);
    const ariaLabel = `Publications du ${dayLabel?.full_date ?? fallbackDate}`;
    const headerLabel = dayLabel?.long_weekday_date ?? fallbackDate;
    const { top, left } = clampDayListPopoverPosition(position);

    return (
        <>
            <button
                type="button"
                className={BACKDROP_CLASS}
                aria-label="Fermer"
                onClick={onClose}
            />
            <div
                data-calendar-day-list-panel
                className={PANEL_CLASS}
                style={{ top, left }}
                role="dialog"
                aria-modal="true"
                aria-label={ariaLabel}
            >
                <div className={HEADER_CLASS}>{headerLabel}</div>
                {publications.length === 0 ? (
                    <p className={EMPTY_CLASS}>Aucune publication ce jour-là.</p>
                ) : (
                    <ul className={LIST_CLASS}>
                        {publications.map((publication, index) => (
                            <li key={publication.id}>
                                <button
                                    type="button"
                                    className={`${ROW_BASE_CLASS} ${getCalendarPillStyle(index)}`}
                                    onMouseEnter={event =>
                                        onPublicationHoverStart(
                                            publication,
                                            publicationHoverAnchorFromRow(event),
                                        )
                                    }
                                    onMouseLeave={onPublicationHoverEnd}
                                    onClick={event =>
                                        onPublicationTap(
                                            publication,
                                            publicationHoverAnchorFromRow(event),
                                        )
                                    }
                                >
                                    <span className="opacity-80">
                                        {formatPublicationTime(publication.publicationDate)}
                                    </span>
                                    <span className="line-clamp-3">
                                        {excerpt(publication.title, 50)}
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </>
    );
}
